import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  Upload,
  Button,
  Switch,
  Image,
  Select,
} from "antd";
import SkeletonTable from "../../components/table/SkeletonTable";
import blogColumns from "../../columns/blogs/blogColumns";
import Plus from "../../assets/Plus";
import ModalComponent from "../../components/global/modals/ModalComponent";
import {
  GetBlogList,
  AddUpdateBlog,
  DeleteBlog,
  UpdateBlogStatus,
} from "../../api/request/blogs";
import toast from "react-hot-toast";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import Edit from "../../assets/Edit";
import Delete from "../../assets/Delete";
import ConfirmationModal from "../../components/global/modals/ConfirmationModal";
import dayjs from "dayjs";
import Search from "../../components/common/Search";
import CKEditorFormEditor from "../../components/common/CKEditorFormEditor";

function Blogs() {
  const [isAddBlog, setIsAddBlog] = useState(false);
  const [blogData, setBlogData] = useState([]); // raw blogs
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isBlogLoading, setIsBlogLoading] = useState(false);

  // delete states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [selectedBlogId, setSelectedBlogId] = useState(null);

  // edit states
  const [editingBlog, setEditingBlog] = useState(null);

  // status states
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedStatusBlog, setSelectedStatusBlog] = useState(null);
  const [isStatusLoading, setIsStatusLoading] = useState(false);


  const fetchBlogs = async (searchText) => {
    try {
      setIsBlogLoading(true);
      const params = {
        search: searchText,
        page: currentPage,
        limit : 6
      };
      const { data } = await GetBlogList(params);
      setTotal(data?.data?.pagination?.total);

      setBlogData(data?.data?.blogs || []);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setIsBlogLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [currentPage]);

  useEffect(() => {
    if (!isAddBlog) {
      form.resetFields();
      setFileList([]);
      setEditingBlog(null);
    }
  }, [isAddBlog]);

  const handleSubmitBlog = async (values) => {
    try {
      setLoading(true);
      const formData = new FormData();
      const apiHeader = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
      };

      formData.append("title", values.title);
      formData.append("desc", values.desc);
      formData.append("date", values.date.format("DD-MM-YYYY"));
      formData.append("status", values.status);

      if (editingBlog) {
        formData.append("id", editingBlog.id);
      }

      if (values.image?.length) {
        const file = values.image[0]?.originFileObj;
        if (file) {
          formData.append("image", file);
        }
      }

      await AddUpdateBlog(formData, apiHeader);
      toast.success(
        editingBlog ? "Blog updated successfully!" : "Blog added successfully!"
      );

      setIsAddBlog(false);
      form.resetFields();
      setFileList([]);
      fetchBlogs();
    } catch (error) {
      console.error(
        editingBlog ? "Error updating blog:" : "Error adding blog:",
        error
      );
      toast.error(error?.response?.data?.message || (editingBlog ? "Failed to update blog" : "Failed to add blog"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    form.setFieldsValue({ image: newFileList });
    if (!newFileList?.length) {
      form.setFieldsValue({ image: [] });
    }
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const deleteBlog = async () => {
    if (!selectedBlogId) return;
    try {
      setIsDeleteLoading(true);
      await DeleteBlog({ id: selectedBlogId });
      toast.success("Blog deleted successfully!");
      setIsDeleteModalOpen(false);
      setSelectedBlogId(null);
      setBlogData((prev) => prev.filter((blog) => blog.id !== selectedBlogId));
    } catch (error) {
      console.error("Error deleting blog:", error);
      toast.error("Failed to delete blog");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const updateBlogStatus = async () => {
    if (!selectedStatusBlog) return;
    try {
      setIsStatusLoading(true);
      const newStatus =
        selectedStatusBlog.status === "active" ? "inactive" : "active";
      await UpdateBlogStatus({ id: selectedStatusBlog.id, status: newStatus });
      toast.success(`Blog status updated to ${newStatus}`);
      setIsStatusModalOpen(false);
      setBlogData((prev) =>
        prev.map((blog) =>
          blog.id === selectedStatusBlog.id
            ? { ...blog, status: newStatus }
            : blog
        )
      );
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update blog status");
    } finally {
      setIsStatusLoading(false);
      setSelectedStatusBlog(null);
    }
  };

  const UploadButton = (
    <button type="button" className="flex flex-col items-center gap-2">
      <div className="size-7">
        <img src="/assets/icons/upload.svg" alt="icon" />
      </div>
      Upload
    </button>
  );

  // transform blogs into table-ready format
  const tableData = blogData.map((item) => ({
    ...item,
    image: (
      <Image
        height={100}
        width={200}
        preview={{ src: item?.image }}
        src={item?.image}
        className="object-contain"
      />
    ),
    status: (
      <Switch
        checked={item.status === "active"}
        checkedChildren="Active"
        unCheckedChildren="Inactive"
        onChange={() => {
          setSelectedStatusBlog(item);
          setIsStatusModalOpen(true);
        }}
      />
    ),
    action: (
      <div className="flex gap-3 items-center justify-start ">
        <GlobalTooltip title="Edit Blog">
          <button
            onClick={() => {
              setEditingBlog(item);
              setIsAddBlog(true);
              form.setFieldsValue({
                title: item.title,
                desc: item.desc,
                date: item.date ? dayjs(item.date) : null,
                status: item.status,
                image: item.image
                  ? [
                      {
                        uid: "-1",
                        name: "blog-image.png",
                        status: "done",
                        url: item.image,
                      },
                    ]
                  : [],
              });
              if (item.image) {
                setFileList([
                  {
                    uid: "-1",
                    name: "blog-image.png",
                    status: "done",
                    url: item.image,
                  },
                ]);
              }
            }}
          >
            <Edit />
          </button>
        </GlobalTooltip>

        <GlobalTooltip title="Delete Blog">
          <button
            className="text-red-500 underline"
            onClick={() => {
              setSelectedBlogId(item?.id);
              setIsDeleteModalOpen(true);
            }}
          >
            <Delete />
          </button>
        </GlobalTooltip>
      </div>
    ),
    desc: (
      <div
        className="w-[300px] truncate line-clamp-2 prose whitespace-break-spaces"
        dangerouslySetInnerHTML={{ __html: item?.desc }}
      />
    ),
    title: (
      <div className="w-[150px] truncate " title={item?.title}>
        {item?.title}
      </div>
    ),
    date: item?.date ? dayjs(item?.date).format("DD-MM-YYYY") : "--",
    created_at: item?.created_at
      ? dayjs(item?.created_at).format("DD-MM-YYYY")
      : "--",
  }));

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination?.current);
  };

  return (
    <>
      <div className="border bg-white rounded-xl py-3">
        <div className="flex items-center justify-between sm:px-4 max-sm:px-2 mb-1">
          <h2 className="font-semibold text-xl">Blogs</h2>
          <div className="block gap-3 items-center sm:flex">
            <div className="md:w-auto w-40">
              <Search handleSearch={fetchBlogs} />
            </div>
            <button
              className="ms-auto flex m-2 px-4 py-2 bg-secondary text-white rounded-md"
              onClick={() => setIsAddBlog(true)}
            >
              <Plus /> Add Blog
            </button>
          </div>
        </div>
        <SkeletonTable
          columns={blogColumns}
          data={tableData}
          isLoading={isBlogLoading}
          pagination={{
            current: currentPage,
            total: total,
            pageSize: 6,
            showSizeChanger: false,
          }}
          handleTableChange={handleTableChange}
        />
      </div>

      {/* Add/Edit Blog Modal */}
      <ModalComponent
        isOpen={isAddBlog}
        setIsOpen={setIsAddBlog}
        title={editingBlog ? "Edit Blog" : "Add Blog"}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitBlog}
          initialValues={{ status: "active" }}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Please enter title" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Description"
            name="desc"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <CKEditorFormEditor />
          </Form.Item>

          <div className="grid grid-cols-2 gap-5">
            <Form.Item
              label="Date"
              name="date"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker format="DD-MM-YYYY" className="w-full" />
            </Form.Item>

            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: "Please select status" }]}
            >
              <Select
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
              />
            </Form.Item>
          </div>

          <div className="col-span-2">
            <Form.Item
              name="image"
              label="Blog Image"
              rules={[
                {
                  required: !editingBlog,
                  message: "Please upload blog image",
                },
              ]}
            >
              <Upload
                beforeUpload={() => false}
                multiple={false}
                listType="picture-card"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={handleChange}
              >
                {fileList.length >= 1 ? null : UploadButton}
              </Upload>
            </Form.Item>

            {previewImage && (
              <Image
                wrapperStyle={{ display: "none" }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
          </div>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="w-full"
            >
              {editingBlog ? "Update" : "Submit"}
            </Button>
          </Form.Item>
        </Form>
      </ModalComponent>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        message={`Are you sure you want to delete this blog?`}
        onConfirm={deleteBlog}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting blog..."
      />

      {/* Status Confirmation Modal */}
      <ConfirmationModal
        isOpen={isStatusModalOpen}
        setIsOpen={setIsStatusModalOpen}
        message={`Are you sure you want to set this blog as ${
          selectedStatusBlog?.status === "active" ? "Inactive" : "Active"
        }?`}
        onConfirm={updateBlogStatus}
        isLoading={isStatusLoading}
        loadingLabel="Updating status..."
      />
    </>
  );
}

export default Blogs;
